import { gsap } from "gsap";

export type GridProfileName = "mobile" | "tablet" | "laptop" | "wide";

export interface GridProfile {
  readonly name: GridProfileName;
  readonly columns: 1 | 2 | 4 | 6;
}

export interface WidgetRect {
  readonly column: number;
  readonly row: number;
  readonly width: number;
  readonly height: number;
}

const MOBILE: GridProfile = { name: "mobile", columns: 1 };
const TABLET: GridProfile = { name: "tablet", columns: 2 };
const LAPTOP: GridProfile = { name: "laptop", columns: 4 };
const WIDE: GridProfile = { name: "wide", columns: 6 };
const LAYOUT_STORAGE_KEY = "shell.widget-layout.v1";

interface StoredLayouts {
  readonly version: 1;
  readonly profiles: Partial<Record<GridProfileName, Record<string, WidgetRect>>>;
}

type LayoutStorage = Pick<Storage, "getItem" | "setItem">;

export const profileForWidth = (width: number): GridProfile => {
  if (width < 600) return MOBILE;
  if (width < 900) return TABLET;
  if (width < 1400) return LAPTOP;
  return WIDE;
};

export const defaultWidgetRects = (count: number, columns: number): readonly WidgetRect[] => {
  if (count <= 0) return [];
  if (count === 1) return [{ column: 0, row: 0, width: columns, height: 2 }];
  if (columns === 1) {
    return Array.from({ length: count }, (_, row) => ({ column: 0, row, width: 1, height: 1 }));
  }
  const half = Math.floor(columns / 2);
  if (count === 2) {
    return [
      { column: 0, row: 0, width: half, height: 2 },
      { column: half, row: 0, width: columns - half, height: 2 }
    ];
  }
  return Array.from({ length: count }, (_, index) => ({
    column: index % 2 === 0 ? 0 : half,
    row: Math.floor(index / 2),
    width: index % 2 === 0 ? half : columns - half,
    height: 1
  }));
};

export const rectsOverlap = (a: WidgetRect, b: WidgetRect): boolean =>
  a.column < b.column + b.width &&
  a.column + a.width > b.column &&
  a.row < b.row + b.height &&
  a.row + a.height > b.row;

export const transferReplacementRect = <T>(
  rects: Map<T, WidgetRect>,
  replacement: T,
  replaced: T
): WidgetRect | undefined => {
  const rect = rects.get(replaced);
  if (!rect) return undefined;
  rects.delete(replaced);
  rects.set(replacement, rect);
  return rect;
};

export const canPlaceRect = (
  candidate: WidgetRect,
  columns: number,
  occupied: readonly WidgetRect[]
): boolean => candidate.column >= 0 && candidate.row >= 0 && candidate.width >= 1 && candidate.height >= 1 &&
  candidate.column + candidate.width <= columns && !occupied.some((rect) => rectsOverlap(candidate, rect));

type ResizeEdge = "inline-start" | "inline" | "block" | "both";

interface ResizeState {
  readonly element: HTMLElement;
  readonly edge: ResizeEdge;
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly startRect: WidgetRect;
  readonly startRects: ReadonlyMap<HTMLElement, WidgetRect>;
}

interface MoveState {
  readonly element: HTMLElement;
  readonly toolbar: HTMLElement;
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly startScrollY: number;
  readonly startRect: WidgetRect;
  readonly grabOffsetX: number;
  readonly grabOffsetY: number;
  readonly containerLeft: number;
  readonly containerTop: number;
  readonly columnStride: number;
  readonly rowStride: number;
  readonly setX: (value: number) => void;
  readonly setY: (value: number) => void;
  currentX: number;
  currentY: number;
  candidate: WidgetRect;
  placement: RelocationResult;
}

export type RelocationResult =
  | { readonly kind: "move"; readonly updates: ReadonlyMap<number, WidgetRect> }
  | { readonly kind: "swap"; readonly updates: ReadonlyMap<number, WidgetRect> }
  | { readonly kind: "pack"; readonly updates: ReadonlyMap<number, WidgetRect> }
  | { readonly kind: "reject"; readonly updates: ReadonlyMap<number, WidgetRect> };

const firstAvailableRect = (
  widget: WidgetRect,
  columns: number,
  occupied: readonly WidgetRect[]
): WidgetRect | undefined => {
  if (widget.width > columns) return undefined;
  const lastOccupiedRow = occupied.reduce((last, rect) => Math.max(last, rect.row + rect.height), 0);
  for (let row = 0; row <= lastOccupiedRow; row += 1) {
    for (let column = 0; column <= columns - widget.width; column += 1) {
      const candidate = { ...widget, column, row };
      if (canPlaceRect(candidate, columns, occupied)) return candidate;
    }
  }
  return undefined;
};

export interface VisibleGridRange {
  readonly startRow: number;
  readonly endRow: number;
}

export const dragScrollDirection = (
  pointerY: number,
  viewportHeight: number,
  topOffset: number,
  edgeSize = 72
): -1 | 0 | 1 => {
  const zone = Math.max(0, Math.min(edgeSize, (viewportHeight - topOffset) / 2));
  if (pointerY <= topOffset + zone) return -1;
  if (pointerY >= viewportHeight - zone) return 1;
  return 0;
};

export const clampDragRowToPage = (
  row: number,
  widgetHeight: number,
  page: number,
  rowsPerPage: number
): number => {
  const pageStart = page * rowsPerPage;
  return Math.max(pageStart, Math.min(pageStart + Math.max(0, rowsPerPage - widgetHeight), row));
};

export const snapPageStartRows = (rects: readonly WidgetRect[], rowsPerPage: number): readonly number[] => {
  if (rowsPerPage < 1 || rects.length === 0) return [];
  const occupiedRows = rects.reduce((last, rect) => Math.max(last, rect.row + rect.height), 0);
  return Array.from({ length: Math.ceil(occupiedRows / rowsPerPage) }, (_, page) => page * rowsPerPage);
};

export const widgetPage = (rect: WidgetRect, rowsPerPage: number): number =>
  rowsPerPage < 1 ? 0 : Math.floor(rect.row / rowsPerPage);

export interface WorkspaceCompaction {
  readonly rects: readonly WidgetRect[];
  readonly removedPages: readonly number[];
  readonly targetPage?: number;
}

const occupiedWorkspacePages = (rects: readonly WidgetRect[], rowsPerPage: number): Set<number> => {
  const pages = new Set<number>();
  if (rowsPerPage < 1) return pages;
  for (const rect of rects) {
    const first = widgetPage(rect, rowsPerPage);
    const last = widgetPage({ ...rect, row: rect.row + rect.height - 1, height: 1 }, rowsPerPage);
    for (let page = first; page <= last; page += 1) pages.add(page);
  }
  return pages;
};

export const compactEmptiedWorkspaces = (
  remaining: readonly WidgetRect[],
  removed: readonly WidgetRect[],
  rowsPerPage: number
): WorkspaceCompaction => {
  if (rowsPerPage < 1 || removed.length === 0) return { rects: remaining, removedPages: [] };
  const occupied = occupiedWorkspacePages(remaining, rowsPerPage);
  const touched = occupiedWorkspacePages(removed, rowsPerPage);
  const removedPages = [...touched].filter((page) => !occupied.has(page)).sort((a, b) => a - b);
  if (removedPages.length === 0) return { rects: remaining, removedPages };

  const compacted = remaining.map((rect) => {
    const removedBefore = removedPages.filter((page) => (page + 1) * rowsPerPage <= rect.row).length;
    return removedBefore === 0 ? rect : { ...rect, row: rect.row - removedBefore * rowsPerPage };
  });
  const firstRemoved = removedPages[0]!;
  const previousPage = [...occupied].filter((page) => page < firstRemoved).sort((a, b) => b - a)[0];
  const nextPage = [...occupied].filter((page) => page > firstRemoved).sort((a, b) => a - b)[0];
  const originalTarget = previousPage ?? nextPage;
  const targetPage = originalTarget === undefined
    ? undefined
    : originalTarget - removedPages.filter((page) => page < originalTarget).length;
  return { rects: compacted, removedPages, ...(targetPage === undefined ? {} : { targetPage }) };
};

export const nextFullscreenRect = (
  current: WidgetRect,
  occupied: readonly WidgetRect[],
  columns: number,
  rowsPerPage: number
): WidgetRect | undefined => {
  if (columns < 1 || rowsPerPage < 1) return undefined;
  const firstPage = Math.floor(current.row / rowsPerPage);
  const lastOccupiedRow = occupied.reduce((last, rect) => Math.max(last, rect.row + rect.height), 0);
  const lastPage = Math.max(firstPage, Math.ceil(lastOccupiedRow / rowsPerPage));
  for (let page = firstPage; page <= lastPage; page += 1) {
    const candidate = { column: 0, row: page * rowsPerPage, width: columns, height: rowsPerPage };
    if (canPlaceRect(candidate, columns, occupied)) return candidate;
  }
  return undefined;
};

export const visibleGridRange = (
  containerTop: number,
  viewportTop: number,
  viewportBottom: number,
  rowHeight: number,
  rowGap: number
): VisibleGridRange => {
  const stride = rowHeight + rowGap;
  if (rowHeight <= 0 || stride <= 0 || viewportBottom <= viewportTop) return { startRow: 0, endRow: 0 };
  const startRow = Math.max(0, Math.ceil((viewportTop - containerTop) / stride));
  const lastRow = Math.floor((viewportBottom - containerTop - rowHeight) / stride);
  return { startRow, endRow: Math.max(startRow, lastRow + 1) };
};

export interface OpeningPlacement {
  readonly kind: "visible" | "repacked" | "reduced" | "overflow";
  readonly rect: WidgetRect;
  readonly updates: ReadonlyMap<number, WidgetRect>;
}

const rectWithinRows = (rect: WidgetRect, rows: VisibleGridRange): boolean =>
  rect.row >= rows.startRow && rect.row + rect.height <= rows.endRow;

const positionsWithinRows = (
  widget: WidgetRect,
  columns: number,
  rows: VisibleGridRange
): readonly WidgetRect[] => {
  const positions: WidgetRect[] = [];
  for (let row = rows.startRow; row + widget.height <= rows.endRow; row += 1) {
    for (let column = 0; column + widget.width <= columns; column += 1) {
      positions.push({ ...widget, column, row });
    }
  }
  return positions;
};

const repackForCandidate = (
  candidate: WidgetRect,
  occupied: readonly WidgetRect[],
  columns: number,
  rows: VisibleGridRange,
  moveAll = false
): ReadonlyMap<number, WidgetRect> | undefined => {
  const displaced = occupied
    .map((rect, index) => ({ rect, index }))
    .filter(({ rect }) => moveAll || rectsOverlap(candidate, rect) || !rectWithinRows(rect, rows));
  if (displaced.length === 0) return new Map();
  const displacedIndexes = new Set(displaced.map(({ index }) => index));
  const placed = occupied.filter((_rect, index) => !displacedIndexes.has(index));
  placed.push(candidate);
  const updates = new Map<number, WidgetRect>();
  const ordered = [...displaced].sort((a, b) => b.rect.width * b.rect.height - a.rect.width * a.rect.height);
  const placeNext = (index: number): boolean => {
    const item = ordered[index];
    if (!item) return true;
    const positions = [...positionsWithinRows(item.rect, columns, rows)].sort((a, b) => {
      const aDistance = Math.abs(a.column - item.rect.column) + Math.abs(a.row - item.rect.row);
      const bDistance = Math.abs(b.column - item.rect.column) + Math.abs(b.row - item.rect.row);
      return aDistance - bDistance || a.row - b.row || a.column - b.column;
    });
    for (const target of positions) {
      if (!canPlaceRect(target, columns, placed)) continue;
      placed.push(target);
      const changed = target.column !== item.rect.column || target.row !== item.rect.row;
      if (changed) updates.set(item.index, target);
      if (placeNext(index + 1)) return true;
      if (changed) updates.delete(item.index);
      placed.pop();
    }
    return false;
  };
  if (!placeNext(0)) return undefined;
  return updates;
};

export const resolveOpeningPlacement = (
  template: WidgetRect,
  occupied: readonly WidgetRect[],
  columns: number,
  rows: VisibleGridRange,
  preferred?: WidgetRect
): OpeningPlacement => {
  const fullSize = { ...template, width: Math.min(template.width, columns) };
  const fullCandidates = positionsWithinRows(fullSize, columns, rows);
  const visiblePreferred = preferred && rectWithinRows(preferred, rows) ? preferred : undefined;
  const directCandidates = visiblePreferred
    ? [visiblePreferred, ...fullCandidates.filter((candidate) => candidate.column !== visiblePreferred.column || candidate.row !== visiblePreferred.row)]
    : fullCandidates;
  const direct = directCandidates.find((candidate) => canPlaceRect(candidate, columns, occupied));
  if (direct) return { kind: "visible", rect: direct, updates: new Map() };

  let bestRepack: OpeningPlacement | undefined;
  for (const candidate of fullCandidates) {
    const updates = repackForCandidate(candidate, occupied, columns, rows);
    if (!updates) continue;
    if (!bestRepack || updates.size < bestRepack.updates.size) {
      bestRepack = { kind: "repacked", rect: candidate, updates };
    }
  }
  if (bestRepack) return bestRepack;
  for (const candidate of fullCandidates) {
    const updates = repackForCandidate(candidate, occupied, columns, rows, true);
    if (updates) return { kind: "repacked", rect: candidate, updates };
  }

  const minimum = { ...fullSize, width: 1, height: 1 };
  const minimumCandidates = positionsWithinRows(minimum, columns, rows);
  const reduced = minimumCandidates.find((candidate) => canPlaceRect(candidate, columns, occupied));
  if (reduced) return { kind: "reduced", rect: reduced, updates: new Map() };
  for (const candidate of minimumCandidates) {
    const updates = repackForCandidate(candidate, occupied, columns, rows);
    if (updates) return { kind: "reduced", rect: candidate, updates };
  }
  for (const candidate of minimumCandidates) {
    const updates = repackForCandidate(candidate, occupied, columns, rows, true);
    if (updates) return { kind: "reduced", rect: candidate, updates };
  }

  const lastOccupiedRow = occupied.reduce((last, rect) => Math.max(last, rect.row + rect.height), 0);
  return {
    kind: "overflow",
    rect: { ...fullSize, column: 0, row: Math.max(lastOccupiedRow, rows.endRow) },
    updates: new Map()
  };
};

export const resolveRelocation = (
  movingIndex: number,
  candidate: WidgetRect,
  rects: readonly WidgetRect[],
  columns: number
): RelocationResult => {
  const start = rects[movingIndex];
  if (!start || candidate.column < 0 || candidate.row < 0 || candidate.column + candidate.width > columns) {
    return { kind: "reject", updates: new Map() };
  }
  const collisions = rects
    .map((rect, index) => ({ rect, index }))
    .filter(({ rect, index }) => index !== movingIndex && rectsOverlap(candidate, rect));
  if (collisions.length === 0) return { kind: "move", updates: new Map([[movingIndex, candidate]]) };
  if (collisions.length === 1) {
    const collision = collisions[0]!;
    const sameSize = collision.rect.width === candidate.width && collision.rect.height === candidate.height;
    const exactTarget = collision.rect.column === candidate.column && collision.rect.row === candidate.row;
    if (sameSize && exactTarget) {
      return {
        kind: "swap",
        updates: new Map([[movingIndex, candidate], [collision.index, start]])
      };
    }
  }

  const displaced = new Set(collisions.map(({ index }) => index));
  const occupied = rects.filter((_rect, index) => index !== movingIndex && !displaced.has(index));
  occupied.push(candidate);
  const updates = new Map<number, WidgetRect>([[movingIndex, candidate]]);
  for (const collision of collisions) {
    const packed = firstAvailableRect(collision.rect, columns, occupied);
    if (!packed) return { kind: "reject", updates: new Map() };
    updates.set(collision.index, packed);
    occupied.push(packed);
  }
  return { kind: "pack", updates };
};

const widgetElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.children).filter((child): child is HTMLElement => child instanceof HTMLElement &&
    child.classList.contains("napplet-window") && !child.hidden && child.dataset.layoutPending !== "true");

const widgetKeys = (elements: readonly HTMLElement[]): ReadonlyMap<HTMLElement, string> => {
  const occurrences = new Map<string, number>();
  return new Map(elements.map((element) => {
    const base = element.querySelector<HTMLIFrameElement>("iframe")?.title.trim() ||
      element.querySelector<HTMLElement>(".napplet-window-title")?.textContent?.trim() || "napplet";
    const occurrence = occurrences.get(base) ?? 0;
    occurrences.set(base, occurrence + 1);
    return [element, `${base}#${occurrence}`];
  }));
};

const readStoredLayouts = (storage: LayoutStorage): StoredLayouts => {
  try {
    const value: unknown = JSON.parse(storage.getItem(LAYOUT_STORAGE_KEY) ?? "null");
    if (typeof value === "object" && value !== null && "version" in value && value.version === 1 &&
      "profiles" in value && typeof value.profiles === "object" && value.profiles !== null) {
      return value as StoredLayouts;
    }
  } catch {
    // Storage may be unavailable or contain stale data. Default layout remains usable.
  }
  return { version: 1, profiles: {} };
};

const applyRect = (element: HTMLElement, rect: WidgetRect): void => {
  element.style.gridColumn = `${rect.column + 1} / span ${rect.width}`;
  element.style.gridRow = `${rect.row + 1} / span ${rect.height}`;
};

const makeHandle = (edge: ResizeEdge, title: string): HTMLButtonElement => {
  const handle = document.createElement("button");
  handle.type = "button";
  handle.className = `napplet-resize-handle napplet-resize-${edge}`;
  handle.dataset.resizeEdge = edge;
  handle.setAttribute("aria-label", title);
  handle.setAttribute("role", "separator");
  handle.setAttribute("aria-orientation", edge === "block" ? "horizontal" : "vertical");
  return handle;
};

export interface WidgetGridController {
  destroy(): void;
  prepareFullscreen(element: HTMLElement): void;
  reveal(element: HTMLElement): void;
  reset(): void;
}

export const createWidgetGrid = (
  container: HTMLElement,
  reducedMotion: MediaQueryList = window.matchMedia("(prefers-reduced-motion: reduce)"),
  storage: LayoutStorage = window.localStorage,
  screenNavigation?: HTMLElement
): WidgetGridController => {
  let profile = profileForWidth(container.getBoundingClientRect().width);
  const storedLayouts = readStoredLayouts(storage);
  const rects = new Map<HTMLElement, WidgetRect>();
  let resize: ResizeState | null = null;
  let move: MoveState | null = null;
  let layoutCustomized = false;
  let keyboardMove: { element: HTMLElement; startRect: WidgetRect; candidate: WidgetRect; placement: RelocationResult } | null = null;
  const preview = document.createElement("div");
  preview.className = "napplet-pack-preview-layer";
  preview.setAttribute("aria-hidden", "true");
  const snapTargets = document.createElement("div");
  snapTargets.className = "window-snap-targets";
  snapTargets.setAttribute("aria-hidden", "true");
  container.append(preview, snapTargets);
  const scrollPosition = { y: window.scrollY };
  let scrollTween: gsap.core.Tween | null = null;
  let dragEdgeDelay: gsap.core.Tween | null = null;
  let dragEdgeDirection: -1 | 0 | 1 = 0;
  let activePageFrame = 0;

  const pageTopOffset = (): number => {
    const rootStyles = getComputedStyle(document.documentElement);
    const barHeight = Number.parseFloat(rootStyles.getPropertyValue("--bar-height")) || 0;
    const pageInset = Number.parseFloat(rootStyles.getPropertyValue("--page-top-inset")) || 0;
    return barHeight + pageInset;
  };

  const closestSnapPage = (): number => {
    const targets = Array.from(snapTargets.children) as HTMLElement[];
    return targets.reduce((closest, target, index) =>
      Math.abs(target.getBoundingClientRect().top - pageTopOffset()) <
        Math.abs((targets[closest]?.getBoundingClientRect().top ?? 0) - pageTopOffset()) ? index : closest, 0);
  };

  const updateActivePage = (): void => {
    activePageFrame = 0;
    if (!screenNavigation) return;
    const activeIndex = closestSnapPage();
    screenNavigation.querySelectorAll<HTMLButtonElement>(".screen-preview").forEach((button, index) => {
      if (index === activeIndex) button.setAttribute("aria-current", "true");
      else button.removeAttribute("aria-current");
    });
  };

  const scheduleActivePageUpdate = (): void => {
    if (!activePageFrame) activePageFrame = window.requestAnimationFrame(updateActivePage);
  };

  const scrollToPage = (page: number, onComplete?: () => void): void => {
    const target = snapTargets.children[page];
    if (!(target instanceof HTMLElement)) return;
    const destination = Math.max(0, window.scrollY + target.getBoundingClientRect().top - pageTopOffset());
    scrollTween?.kill();
    if (reducedMotion.matches) {
      window.scrollTo(0, destination);
      scheduleActivePageUpdate();
      onComplete?.();
      return;
    }
    scrollPosition.y = window.scrollY;
    document.documentElement.dataset.screenScrolling = "true";
    scrollTween = gsap.to(scrollPosition, {
      y: destination,
      duration: .46,
      ease: "power4.out",
      overwrite: true,
      onUpdate: () => window.scrollTo(0, scrollPosition.y),
      onComplete: () => {
        delete document.documentElement.dataset.screenScrolling;
        window.scrollTo(0, destination);
        scrollTween = null;
        scheduleActivePageUpdate();
        onComplete?.();
      },
      onInterrupt: () => {
        delete document.documentElement.dataset.screenScrolling;
        scrollTween = null;
      }
    });
  };

  const onScreenNavigationClick = (event: MouseEvent): void => {
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>(".screen-preview") : null;
    if (!button || !screenNavigation?.contains(button)) return;
    scrollToPage(Number(button.dataset.page));
  };

  const fullscreen = (element: HTMLElement): boolean => {
    const current = rects.get(element);
    if (!current) return false;
    const rowsPerPage = profile.name === "mobile" ? 1 : 2;
    const destination = nextFullscreenRect(current, occupiedExcept(element), profile.columns, rowsPerPage);
    if (!destination) return false;
    const before = element.getBoundingClientRect();
    if (!commitRects(new Map([[element, destination]]))) return false;
    layoutCustomized = true;
    persistCurrentLayout();
    const button = element.querySelector<HTMLButtonElement>(".napplet-window-fullscreen");
    button?.setAttribute("aria-label", `Fullscreen ${element.querySelector<HTMLElement>(".napplet-window-title")?.textContent?.trim() || "Napplet"}`);
    const page = destination.row / rowsPerPage;
    if (reducedMotion.matches) {
      scrollToPage(page);
      return true;
    }
    const after = element.getBoundingClientRect();
    gsap.fromTo(element, {
      x: before.left - after.left,
      y: before.top - after.top,
      scaleX: before.width / after.width,
      scaleY: before.height / after.height,
      transformOrigin: "top left"
    }, {
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      duration: .32,
      ease: "power4.out",
      clearProps: "transform,transformOrigin",
      overwrite: "auto",
      onComplete: () => scrollToPage(page)
    });
    return true;
  };

  const onFullscreenClick = (event: MouseEvent): void => {
    const button = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>(".napplet-window-fullscreen")
      : null;
    const element = button?.closest<HTMLElement>(".napplet-window");
    if (element) fullscreen(element);
  };

  const syncScreenNavigation = (pageRows: readonly number[], rowsPerPage: number): void => {
    if (!screenNavigation) return;
    const entries = [...rects];
    const buttons = pageRows.map((pageRow, page) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "screen-preview";
      button.dataset.page = String(page);
      button.style.setProperty("--screen-columns", String(profile.columns));
      button.style.setProperty("--screen-rows", String(rowsPerPage));
      const pageEnd = pageRow + rowsPerPage;
      const visible = entries.filter(([, rect]) => rect.row < pageEnd && rect.row + rect.height > pageRow);
      const titles = visible.map(([element]) =>
        element.querySelector<HTMLElement>(".napplet-window-title")?.textContent?.trim() || "Napplet");
      button.setAttribute("aria-label", `Screen ${page + 1}${titles.length ? `: ${titles.join(", ")}` : ""}`);
      button.title = button.getAttribute("aria-label") ?? "";
      for (const [, rect] of visible) {
        const miniature = document.createElement("span");
        const clippedStart = Math.max(rect.row, pageRow);
        const clippedEnd = Math.min(rect.row + rect.height, pageEnd);
        miniature.className = "screen-preview-window";
        miniature.style.gridColumn = `${rect.column + 1} / span ${rect.width}`;
        miniature.style.gridRow = `${clippedStart - pageRow + 1} / span ${clippedEnd - clippedStart}`;
        miniature.setAttribute("aria-hidden", "true");
        button.append(miniature);
      }
      return button;
    });
    screenNavigation.replaceChildren(...buttons);
    scheduleActivePageUpdate();
  };

  const syncSnapTargets = (): void => {
    const rowsPerPage = profile.name === "mobile" ? 1 : 2;
    const pageRows = snapPageStartRows([...rects.values()], rowsPerPage);
    const targets = Array.from(snapTargets.children) as HTMLElement[];
    pageRows.forEach((row, index) => {
      const target = targets[index] ?? document.createElement("span");
      target.className = "window-snap-target";
      target.style.gridRow = `${row + 1} / span ${rowsPerPage}`;
      if (!target.parentElement) snapTargets.append(target);
    });
    targets.slice(pageRows.length).forEach((target) => target.remove());
    syncScreenNavigation(pageRows, rowsPerPage);
  };

  const occupiedExcept = (element: HTMLElement): WidgetRect[] =>
    [...rects].filter(([candidate]) => candidate !== element).map(([, rect]) => rect);

  const updateHandleValues = (element: HTMLElement, rect: WidgetRect): void => {
    const inlineStart = element.querySelector<HTMLElement>(".napplet-resize-inline-start");
    const inline = element.querySelector<HTMLElement>(".napplet-resize-inline");
    const block = element.querySelector<HTMLElement>(".napplet-resize-block");
    const both = element.querySelector<HTMLElement>(".napplet-resize-both");
    for (const handle of [inlineStart, inline, both]) {
      handle?.setAttribute("aria-valuemin", "1");
      handle?.setAttribute("aria-valuemax", String(profile.columns - rect.column));
      handle?.setAttribute("aria-valuenow", String(rect.width));
    }
    block?.setAttribute("aria-valuemin", "1");
    block?.setAttribute("aria-valuemax", "12");
    block?.setAttribute("aria-valuenow", String(rect.height));
  };

  const commitRects = (updates: ReadonlyMap<HTMLElement, WidgetRect>): boolean => {
    const next = new Map(rects);
    for (const [element, rect] of updates) next.set(element, rect);
    const entries = [...next];
    const valid = entries.every(([element, rect], index) =>
      canPlaceRect(rect, profile.columns, entries.filter(([candidate], otherIndex) => candidate !== element && otherIndex !== index).map(([, other]) => other))
    );
    if (!valid) return false;
    for (const [element, rect] of updates) {
      rects.set(element, rect);
      applyRect(element, rect);
      updateHandleValues(element, rect);
    }
    syncSnapTargets();
    return true;
  };

  const setRect = (element: HTMLElement, rect: WidgetRect): boolean =>
    commitRects(new Map([[element, rect]]));

  const writeStoredLayouts = (): void => {
    try {
      storage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(storedLayouts));
    } catch {
      // Storage failure must not break moving, resizing, or opening napplets.
    }
  };

  const persistCurrentLayout = (): void => {
    if (!layoutCustomized) return;
    const elements = widgetElements(container);
    const keys = widgetKeys(elements);
    const saved = { ...(storedLayouts.profiles[profile.name] ?? {}) };
    for (const element of elements) {
      const key = keys.get(element);
      const rect = rects.get(element);
      if (key && rect) saved[key] = rect;
    }
    storedLayouts.profiles[profile.name] = saved;
    writeStoredLayouts();
  };

  const clearStoredProfile = (): void => {
    delete storedLayouts.profiles[profile.name];
    writeStoredLayouts();
  };

  const placementFor = (element: HTMLElement, candidate: WidgetRect): RelocationResult => {
    const entries = [...rects];
    const movingIndex = entries.findIndex(([candidateElement]) => candidateElement === element);
    return resolveRelocation(movingIndex, candidate, entries.map(([, rect]) => rect), profile.columns);
  };

  const previewCard = (element: HTMLElement, rect: WidgetRect, placement: RelocationResult["kind"]): HTMLElement => {
    const card = document.createElement("article");
    const toolbar = document.createElement("header");
    const title = document.createElement("span");
    const content = document.createElement("div");
    card.className = "napplet-pack-preview";
    card.dataset.placement = placement;
    toolbar.className = "napplet-pack-preview-toolbar";
    title.className = "napplet-pack-preview-title";
    title.textContent = element.querySelector<HTMLElement>(".napplet-window-title")?.textContent ?? "Napplet";
    content.className = "napplet-pack-preview-content";
    toolbar.append(title);
    card.append(toolbar, content);
    applyRect(card, rect);
    return card;
  };

  const showPreview = (element: HTMLElement, candidate: WidgetRect, placement: RelocationResult): void => {
    const entries = [...rects];
    const cards: HTMLElement[] = [];
    if (placement.kind === "reject") {
      cards.push(previewCard(element, candidate, placement.kind));
    } else {
      for (const [index, rect] of placement.updates) {
        const target = entries[index]?.[0];
        if (target) cards.push(previewCard(target, rect, placement.kind));
      }
    }
    preview.replaceChildren(...cards);
  };

  const hidePreview = (): void => {
    preview.replaceChildren();
  };

  const animateDropConfirmation = (elements: readonly HTMLElement[]): void => {
    if (reducedMotion.matches) return;
    gsap.fromTo(elements, {
      scale: .99,
      filter: "brightness(1.06)"
    }, {
      scale: 1,
      filter: "brightness(1)",
      duration: .16,
      ease: "power3.out",
      clearProps: "transform,filter",
      overwrite: "auto"
    });
  };

  const commitPlacement = (element: HTMLElement, placement: RelocationResult): boolean => {
    if (placement.kind === "reject") return false;
    const entries = [...rects];
    const updates = new Map<HTMLElement, WidgetRect>();
    for (const [index, rect] of placement.updates) {
      const target = entries[index]?.[0];
      if (target) updates.set(target, rect);
    }
    gsap.set(element, { clearProps: "transform" });
    if (!commitRects(updates)) return false;
    layoutCustomized = true;
    animateDropConfirmation([...updates.keys()]);
    window.setTimeout(persistCurrentLayout, 0);
    return true;
  };

  const resizeFrom = (
    element: HTMLElement,
    start: WidgetRect,
    widthDelta: number,
    heightDelta: number,
    inlineStartDelta = 0,
    baseRects: ReadonlyMap<HTMLElement, WidgetRect> = rects
  ): void => {
    const updates = new Map<HTMLElement, WidgetRect>();
    const originalRight = start.column + start.width;
    const column = Math.max(0, Math.min(originalRight - 1, start.column + inlineStartDelta));
    const appliedInlineStartDelta = column - start.column;
    const width = Math.max(1, Math.min(profile.columns - column, originalRight - column + widthDelta));
    const height = Math.max(1, start.height + heightDelta);
    const appliedWidthDelta = column + width - originalRight;
    const appliedHeightDelta = height - start.height;
    updates.set(element, { ...start, column, width, height });

    if (appliedInlineStartDelta !== 0) {
      const neighbor = [...baseRects].find(([candidate, rect]) => candidate !== element && rect.column + rect.width === start.column &&
        rect.row < start.row + start.height && rect.row + rect.height > start.row);
      if (neighbor) {
        const [candidate, rect] = neighbor;
        updates.set(candidate, { ...rect, width: rect.width + appliedInlineStartDelta });
      }
    }

    if (appliedWidthDelta !== 0) {
      const boundary = originalRight;
      const neighbor = [...baseRects].find(([candidate, rect]) => candidate !== element && rect.column === boundary &&
        rect.row < start.row + start.height && rect.row + rect.height > start.row);
      if (neighbor) {
        const [candidate, rect] = neighbor;
        updates.set(candidate, { ...rect, column: rect.column + appliedWidthDelta, width: rect.width - appliedWidthDelta });
      }
    }
    if (appliedHeightDelta !== 0) {
      const boundary = start.row + start.height;
      const neighbor = [...baseRects].find(([candidate, rect]) => candidate !== element && rect.row === boundary &&
        rect.column < start.column + start.width && rect.column + rect.width > start.column);
      if (neighbor) {
        const [candidate, rect] = neighbor;
        updates.set(candidate, { ...rect, row: rect.row + appliedHeightDelta, height: rect.height - appliedHeightDelta });
      }
    }
    if (commitRects(updates) && (appliedInlineStartDelta !== 0 || appliedWidthDelta !== 0 || appliedHeightDelta !== 0)) {
      layoutCustomized = true;
      persistCurrentLayout();
    }
  };

  const animateLayout = (elements: readonly HTMLElement[]): void => {
    if (reducedMotion.matches || elements.length === 0) return;
    gsap.fromTo(elements, { autoAlpha: 0, scale: .985, filter: "blur(3px)" }, {
      autoAlpha: 1,
      scale: 1,
      filter: "blur(0px)",
      duration: .28,
      ease: "power4.out",
      clearProps: "opacity,visibility,transform,filter"
    });
  };

  const animateSurfaceReplacement = (elements: readonly HTMLElement[]): void => {
    if (reducedMotion.matches || elements.length === 0) return;
    gsap.fromTo(elements, { autoAlpha: .72, scale: .99, filter: "blur(2px)" }, {
      autoAlpha: 1,
      scale: 1,
      filter: "blur(0px)",
      duration: .2,
      ease: "power4.out",
      clearProps: "opacity,visibility,transform,filter",
      overwrite: "auto"
    });
  };

  const currentVisibleRows = (): VisibleGridRange => {
    const containerRect = container.getBoundingClientRect();
    const styles = getComputedStyle(container);
    const rowHeight = Number.parseFloat(styles.gridAutoRows) || 220;
    const rowGap = Number.parseFloat(styles.rowGap) || 0;
    const visualViewport = window.visualViewport;
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const viewportHeight = visualViewport?.height ?? window.innerHeight;
    return visibleGridRange(containerRect.top, viewportTop, viewportTop + viewportHeight, rowHeight, rowGap);
  };

  const commitOpeningPlacement = (element: HTMLElement, placement: OpeningPlacement): boolean => {
    const entries = [...rects];
    const updates = new Map<HTMLElement, WidgetRect>([[element, placement.rect]]);
    for (const [index, rect] of placement.updates) {
      const target = entries[index]?.[0];
      if (target) updates.set(target, rect);
    }
    const moved = [...updates.keys()].filter((target) => target !== element);
    const before = new Map(moved.map((target) => [target, target.getBoundingClientRect()]));
    if (!commitRects(updates)) return false;
    if (!reducedMotion.matches) {
      for (const target of moved) {
        const previous = before.get(target);
        if (!previous) continue;
        const next = target.getBoundingClientRect();
        gsap.fromTo(target, { x: previous.left - next.left, y: previous.top - next.top }, {
          x: 0,
          y: 0,
          duration: .28,
          ease: "power4.out",
          clearProps: "transform",
          overwrite: "auto"
        });
      }
    }
    return true;
  };

  const reset = (animate = true, clearPersistence = false): void => {
    const elements = widgetElements(container);
    if (clearPersistence) clearStoredProfile();
    layoutCustomized = false;
    rects.clear();
    defaultWidgetRects(elements.length, profile.columns).forEach((rect, index) => {
      const element = elements[index];
      if (element) setRect(element, rect);
    });
    if (animate) animateLayout(elements);
  };

  const decorate = (element: HTMLElement): void => {
    if (element.dataset.widgetResizable === "true") return;
    element.dataset.widgetResizable = "true";
    const toolbar = element.querySelector<HTMLElement>(".napplet-window-toolbar");
    if (toolbar) {
      toolbar.tabIndex = 0;
      toolbar.dataset.widgetDragHandle = "true";
      toolbar.setAttribute("aria-label", "Move window. Press Space, then use arrow keys.");
    }
    element.append(
      makeHandle("inline-start", "Resize window from left edge"),
      makeHandle("inline", "Resize window width"),
      makeHandle("block", "Resize window height"),
      makeHandle("both", "Resize window width and height")
    );
  };

  const sync = (): void => {
    const elements = widgetElements(container);
    const replacements = elements.flatMap((element) => {
      const replacedWindowId = element.dataset.replacesWindowId;
      if (!replacedWindowId) return [];
      const replaced = [...rects.keys()].find((candidate) => candidate.dataset.windowId === replacedWindowId);
      const rect = replaced ? transferReplacementRect(rects, element, replaced) : undefined;
      delete element.dataset.replacesWindowId;
      return rect && replaced ? [{ element, replaced, rect }] : [];
    });
    for (const { element, replaced, rect } of replacements) {
      applyRect(element, rect);
      updateHandleValues(element, rect);
    }
    animateSurfaceReplacement(replacements.map(({ element }) => element));
    const added = elements.filter((element) => !rects.has(element));
    for (const element of elements) decorate(element);
    const removed = [...rects].filter(([element]) => !elements.includes(element));
    for (const [element] of removed) rects.delete(element);
    const rowsPerPage = profile.name === "mobile" ? 1 : 2;
    const compaction = compactEmptiedWorkspaces(
      [...rects.values()],
      removed.map(([, rect]) => rect),
      rowsPerPage
    );
    if (layoutCustomized && compaction.removedPages.length > 0) {
      const entries = [...rects];
      const updates = new Map<HTMLElement, WidgetRect>();
      compaction.rects.forEach((rect, index) => {
        const entry = entries[index];
        if (entry && entry[1].row !== rect.row) updates.set(entry[0], rect);
      });
      const before = new Map([...updates.keys()].map((element) => [element, element.getBoundingClientRect()]));
      if (updates.size > 0 && commitRects(updates) && !reducedMotion.matches) {
        for (const element of updates.keys()) {
          const previous = before.get(element);
          if (!previous) continue;
          const next = element.getBoundingClientRect();
          gsap.fromTo(element, { y: previous.top - next.top }, {
            y: 0,
            duration: .32,
            ease: "power4.out",
            clearProps: "transform",
            overwrite: "auto"
          });
        }
      }
    }
    if (compaction.removedPages.length > 0) syncSnapTargets();
    const scrollAfterEmptyWorkspaceRemoval = (): void => {
      const targetPage = compaction.targetPage;
      if (targetPage === undefined) return;
      const occupiedPages = [...occupiedWorkspacePages([...rects.values()], rowsPerPage)].sort((a, b) => a - b);
      const target = [...occupiedPages].reverse().find((page) => page <= targetPage) ?? occupiedPages[0];
      if (target !== undefined) scrollToPage(target);
    };
    const fullscreenOpening = added.find((element) => element.dataset.openFullscreen === "true");
    if (fullscreenOpening) {
      delete fullscreenOpening.dataset.openFullscreen;
      const visibleRows = currentVisibleRows();
      const page = Math.floor(visibleRows.startRow / rowsPerPage);
      const destination = nextFullscreenRect(
        { column: 0, row: page * rowsPerPage, width: profile.columns, height: rowsPerPage },
        [...rects.values()],
        profile.columns,
        rowsPerPage
      );
      if (destination) {
        commitOpeningPlacement(fullscreenOpening, { kind: "visible", rect: destination, updates: new Map() });
        layoutCustomized = true;
        persistCurrentLayout();
        animateLayout([fullscreenOpening]);
        scrollToPage(widgetPage(destination, rowsPerPage));
        return;
      }
    }
    if (rects.size === 0) {
      const saved = storedLayouts.profiles[profile.name];
      const keys = widgetKeys(elements);
      const defaults = defaultWidgetRects(elements.length, profile.columns);
      let usedSavedLayout = false;
      for (const element of elements) {
        const index = elements.indexOf(element);
        const template = defaults[index] ?? { column: 0, row: 0, width: 1, height: 1 };
        const key = keys.get(element);
        const stored = key ? saved?.[key] : undefined;
        const occupied = [...rects.values()];
        const placement = stored && canPlaceRect(stored, profile.columns, occupied)
          ? { kind: "visible" as const, rect: stored, updates: new Map<number, WidgetRect>() }
          : resolveOpeningPlacement(
            template,
            occupied,
            profile.columns,
            currentVisibleRows(),
            stored
          );
        if (stored && placement.kind === "visible" && placement.rect === stored) usedSavedLayout = true;
        commitOpeningPlacement(element, placement);
      }
      if (usedSavedLayout) {
        layoutCustomized = true;
        persistCurrentLayout();
      }
      syncSnapTargets();
      animateLayout(elements);
      scrollAfterEmptyWorkspaceRemoval();
      return;
    }
    if (!layoutCustomized) {
      reset(true);
      scrollAfterEmptyWorkspaceRemoval();
      return;
    }
    const defaults = defaultWidgetRects(elements.length, profile.columns);
    const saved = storedLayouts.profiles[profile.name];
    const keys = widgetKeys(elements);
    for (const element of added) {
      const index = elements.indexOf(element);
      const template = defaults[index] ?? { column: 0, row: 0, width: 1, height: 1 };
      const stored = saved?.[keys.get(element) ?? ""];
      const occupied = [...rects.values()];
      const placement = stored && canPlaceRect(stored, profile.columns, occupied)
        ? { kind: "visible" as const, rect: stored, updates: new Map<number, WidgetRect>() }
        : resolveOpeningPlacement(
          template,
          occupied,
          profile.columns,
          currentVisibleRows(),
          stored
        );
      commitOpeningPlacement(element, placement);
    }
    persistCurrentLayout();
    animateLayout(added);
    scrollAfterEmptyWorkspaceRemoval();
  };

  const resizeBy = (element: HTMLElement, edge: ResizeEdge, widthDelta: number, heightDelta: number): void => {
    const current = rects.get(element);
    if (!current) return;
    resizeFrom(element, current, edge === "inline-start" ? 0 : widthDelta, heightDelta,
      edge === "inline-start" ? widthDelta : 0);
  };

  const stopDragAutoScroll = (): void => {
    dragEdgeDelay?.kill();
    dragEdgeDelay = null;
    dragEdgeDirection = 0;
  };

  const scheduleDragAutoScroll = (pointerY: number): void => {
    if (!move) return;
    const direction = dragScrollDirection(pointerY, window.innerHeight, pageTopOffset());
    if (direction === 0) {
      stopDragAutoScroll();
      return;
    }
    if (direction === dragEdgeDirection) return;
    dragEdgeDelay?.kill();
    dragEdgeDirection = direction;
    dragEdgeDelay = gsap.delayedCall(.28, () => {
      dragEdgeDelay = null;
      if (!move) return;
      const targets = Array.from(snapTargets.children) as HTMLElement[];
      const currentPage = closestSnapPage();
      const nextPage = Math.max(0, Math.min(targets.length - 1, currentPage + direction));
      if (nextPage === currentPage) {
        dragEdgeDirection = 0;
        return;
      }
      scrollToPage(nextPage, () => {
        if (!move) return;
        dragEdgeDirection = 0;
        scheduleDragAutoScroll(move.currentY);
      });
    });
  };

  const updateMovePosition = (clientX: number, clientY: number, checkEdge = true): void => {
    if (!move) return;
    move.currentX = clientX;
    move.currentY = clientY;
    move.setX(clientX - move.startX);
    move.setY(clientY - move.startY + window.scrollY - move.startScrollY);
    const column = Math.max(0, Math.min(profile.columns - move.startRect.width,
      Math.round((clientX - move.containerLeft - move.grabOffsetX) / move.columnStride)));
    const rawRow = Math.max(0, Math.round((clientY + window.scrollY - move.containerTop - move.grabOffsetY) / move.rowStride));
    const rowsPerPage = profile.name === "mobile" ? 1 : 2;
    const row = clampDragRowToPage(rawRow, move.startRect.height, closestSnapPage(), rowsPerPage);
    if (column !== move.candidate.column || row !== move.candidate.row) {
      move.candidate = { ...move.startRect, column, row };
      move.placement = placementFor(move.element, move.candidate);
      showPreview(move.element, move.candidate, move.placement);
    }
    if (checkEdge) scheduleDragAutoScroll(clientY);
  };

  const onPointerDown = (event: PointerEvent): void => {
    const handle = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-resize-edge]") : null;
    const element = handle?.closest<HTMLElement>(".napplet-window");
    const startRect = element ? rects.get(element) : undefined;
    if (handle && element && startRect && event.button === 0) {
      event.preventDefault();
      const edge = handle.dataset.resizeEdge as ResizeEdge;
      handle.setPointerCapture(event.pointerId);
      resize = { element, edge, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, startRect, startRects: new Map(rects) };
      container.dataset.interacting = "resize";
      element.dataset.resizing = "true";
      return;
    }
    const toolbar = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-widget-drag-handle]") : null;
    const movingElement = toolbar?.closest<HTMLElement>(".napplet-window");
    const movingRect = movingElement ? rects.get(movingElement) : undefined;
    if (!toolbar || !movingElement || !movingRect || event.button !== 0 || event.target instanceof Element && event.target.closest("button, a, input, select, textarea")) return;
    event.preventDefault();
    toolbar.setPointerCapture(event.pointerId);
    const bounds = movingElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const styles = getComputedStyle(container);
    const gap = Number.parseFloat(styles.columnGap) || 0;
    const cellWidth = (containerRect.width - gap * (profile.columns - 1)) / profile.columns;
    const rowHeight = Number.parseFloat(styles.gridAutoRows) || 220;
    const placement = placementFor(movingElement, movingRect);
    move = {
      element: movingElement,
      toolbar,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScrollY: window.scrollY,
      startRect: movingRect,
      grabOffsetX: event.clientX - bounds.left,
      grabOffsetY: event.clientY - bounds.top,
      containerLeft: containerRect.left,
      containerTop: containerRect.top + window.scrollY,
      columnStride: cellWidth + gap,
      rowStride: rowHeight + gap,
      setX: gsap.quickSetter(movingElement, "x", "px") as (value: number) => void,
      setY: gsap.quickSetter(movingElement, "y", "px") as (value: number) => void,
      currentX: event.clientX,
      currentY: event.clientY,
      candidate: movingRect,
      placement
    };
    keyboardMove = null;
    container.dataset.interacting = "move";
    movingElement.dataset.dragging = "true";
    toolbar.setAttribute("aria-grabbed", "true");
    showPreview(movingElement, movingRect, placement);
    if (!reducedMotion.matches) gsap.to(movingElement, { scale: 1.015, duration: .14, ease: "power3.out" });
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (resize && event.pointerId === resize.pointerId) {
      const containerRect = container.getBoundingClientRect();
      const styles = getComputedStyle(container);
      const gap = Number.parseFloat(styles.columnGap) || 0;
      const cellWidth = (containerRect.width - gap * (profile.columns - 1)) / profile.columns;
      const rowHeight = Number.parseFloat(styles.gridAutoRows) || 220;
      const horizontalDelta = Math.round((event.clientX - resize.startX) / (cellWidth + gap));
      const widthDelta = resize.edge === "block" || resize.edge === "inline-start" ? 0 : horizontalDelta;
      const heightDelta = resize.edge === "inline" || resize.edge === "inline-start"
        ? 0
        : Math.round((event.clientY - resize.startY) / (rowHeight + gap));
      const inlineStartDelta = resize.edge === "inline-start" ? horizontalDelta : 0;
      resizeFrom(resize.element, resize.startRect, widthDelta, heightDelta, inlineStartDelta, resize.startRects);
      return;
    }
    if (!move || event.pointerId !== move.pointerId) return;
    updateMovePosition(event.clientX, event.clientY);
  };

  const endDrag = (event: PointerEvent): void => {
    if (resize && event.pointerId === resize.pointerId) {
      const resizeHandle = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-resize-edge]") : null;
      if (resizeHandle?.hasPointerCapture(event.pointerId)) resizeHandle.releasePointerCapture(event.pointerId);
      delete resize.element.dataset.resizing;
      delete container.dataset.interacting;
      resize = null;
      return;
    }
    if (!move || event.pointerId !== move.pointerId) return;
    stopDragAutoScroll();
    scrollTween?.kill();
    const active = move;
    if (active.toolbar.hasPointerCapture(event.pointerId)) active.toolbar.releasePointerCapture(event.pointerId);
    const accepted = event.type !== "pointercancel" && commitPlacement(active.element, active.placement);
    if (!accepted) {
      gsap.to(active.element, {
        x: 0,
        y: 0,
        scale: 1,
        duration: reducedMotion.matches ? 0 : .2,
        ease: "power4.out",
        clearProps: "transform"
      });
    }
    delete active.element.dataset.dragging;
    active.toolbar.removeAttribute("aria-grabbed");
    delete container.dataset.interacting;
    hidePreview();
    move = null;
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    const handle = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-resize-edge]") : null;
    const element = handle?.closest<HTMLElement>(".napplet-window");
    if (!handle || !element) {
      const toolbar = event.target instanceof HTMLElement && event.target.matches("[data-widget-drag-handle]") ? event.target : null;
      const movingElement = toolbar?.closest<HTMLElement>(".napplet-window");
      const current = movingElement ? rects.get(movingElement) : undefined;
      if (!toolbar || !movingElement || !current) return;
      if (!keyboardMove && (event.key === " " || event.key === "Spacebar")) {
        event.preventDefault();
        keyboardMove = { element: movingElement, startRect: current, candidate: current, placement: placementFor(movingElement, current) };
        container.dataset.interacting = "move";
        movingElement.dataset.dragging = "true";
        toolbar.setAttribute("aria-grabbed", "true");
        showPreview(movingElement, current, keyboardMove.placement);
        return;
      }
      if (!keyboardMove || keyboardMove.element !== movingElement) return;
      if (event.key === "Escape") {
        event.preventDefault();
        delete movingElement.dataset.dragging;
        toolbar.removeAttribute("aria-grabbed");
        delete container.dataset.interacting;
        hidePreview();
        keyboardMove = null;
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        commitPlacement(movingElement, keyboardMove.placement);
        delete movingElement.dataset.dragging;
        toolbar.removeAttribute("aria-grabbed");
        delete container.dataset.interacting;
        hidePreview();
        keyboardMove = null;
        return;
      }
      const columnDelta = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
      const rowDelta = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
      if (columnDelta === 0 && rowDelta === 0) return;
      event.preventDefault();
      const candidate = {
        ...keyboardMove.candidate,
        column: Math.max(0, Math.min(profile.columns - current.width, keyboardMove.candidate.column + columnDelta)),
        row: Math.max(0, keyboardMove.candidate.row + rowDelta)
      };
      keyboardMove.candidate = candidate;
      keyboardMove.placement = placementFor(movingElement, candidate);
      showPreview(movingElement, candidate, keyboardMove.placement);
      return;
    }
    const edge = handle.dataset.resizeEdge as ResizeEdge;
    if (event.key === "Home") {
      event.preventDefault();
      reset(true, true);
      return;
    }
    const delta = event.shiftKey ? 2 : 1;
    const widthDelta = edge === "block" ? 0 : event.key === "ArrowRight" ? delta : event.key === "ArrowLeft" ? -delta : 0;
    const heightDelta = edge === "inline" ? 0 : event.key === "ArrowDown" ? delta : event.key === "ArrowUp" ? -delta : 0;
    if (widthDelta === 0 && heightDelta === 0) return;
    event.preventDefault();
    resizeBy(element, edge, widthDelta, heightDelta);
  };

  const mutationObserver = new MutationObserver((records) => {
    if (records.some((record) => record.target === container ||
      (record.type === "attributes" && record.target instanceof HTMLElement && record.target.parentElement === container))) sync();
  });
  mutationObserver.observe(container, {
    childList: true,
    attributes: true,
    attributeFilter: ["hidden", "data-layout-pending", "data-replaces-window-id"],
    subtree: true
  });
  const resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const next = profileForWidth(entry.contentRect.width);
    if (next.name === profile.name) return;
    persistCurrentLayout();
    profile = next;
    container.dataset.gridProfile = profile.name;
    container.style.setProperty("--widget-columns", String(profile.columns));
    rects.clear();
    layoutCustomized = false;
    sync();
  });
  resizeObserver.observe(container);
  container.dataset.gridProfile = profile.name;
  container.style.setProperty("--widget-columns", String(profile.columns));
  container.addEventListener("pointerdown", onPointerDown);
  container.addEventListener("pointermove", onPointerMove);
  container.addEventListener("pointerup", endDrag);
  container.addEventListener("pointercancel", endDrag);
  container.addEventListener("keydown", onKeyDown);
  container.addEventListener("click", onFullscreenClick);
  screenNavigation?.addEventListener("click", onScreenNavigationClick);
  const onWindowScroll = (): void => {
    scheduleActivePageUpdate();
    if (move) updateMovePosition(move.currentX, move.currentY, false);
  };
  window.addEventListener("scroll", onWindowScroll, { passive: true });
  window.addEventListener("resize", scheduleActivePageUpdate);
  sync();

  return {
    reset: () => reset(true, true),
    prepareFullscreen: (element) => { element.dataset.openFullscreen = "true"; },
    reveal: (element) => {
      const revealWhenLaidOut = (remainingFrames: number): void => {
        const rect = rects.get(element);
        if (rect) {
          scrollToPage(widgetPage(rect, profile.name === "mobile" ? 1 : 2));
          return;
        }
        if (remainingFrames > 0) window.requestAnimationFrame(() => revealWhenLaidOut(remainingFrames - 1));
      };
      revealWhenLaidOut(2);
    },
    destroy: () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", endDrag);
      container.removeEventListener("pointercancel", endDrag);
      container.removeEventListener("keydown", onKeyDown);
      container.removeEventListener("click", onFullscreenClick);
      screenNavigation?.removeEventListener("click", onScreenNavigationClick);
      window.removeEventListener("scroll", onWindowScroll);
      window.removeEventListener("resize", scheduleActivePageUpdate);
      if (activePageFrame) window.cancelAnimationFrame(activePageFrame);
      scrollTween?.kill();
      stopDragAutoScroll();
      delete document.documentElement.dataset.screenScrolling;
      screenNavigation?.replaceChildren();
      preview.remove();
      snapTargets.remove();
      rects.clear();
    }
  };
};
